import { z } from 'zod';
import { PersonRefSchema, SiteRefSchema } from './property';

/**
 * Operations › Phones (docs/specs/operations.md §11, D92): the register of company mobile
 * handsets. A branch handset belongs to a seat (the Manager or Assistant Manager position), so
 * whoever holds the seat holds the phone; a handset can instead be given to a named person
 * (driver phones, later) or sit at a site without a seat. The Office 365 password is stored
 * encrypted and revealed one value at a time, audited, never listed.
 */

const Instant = z.iso.datetime();

export const PHONES_PERMISSIONS = {
  view: 'operations.phones.view',
  manage: 'operations.phones.manage',
} as const;

export const PhoneKindSchema = z.enum(['branch_manager', 'branch_assistant', 'driver', 'other']);
export type PhoneKind = z.infer<typeof PhoneKindSchema>;
export const PHONE_KIND_LABELS: Record<PhoneKind, string> = {
  branch_manager: 'Manager handset',
  branch_assistant: 'Assistant handset',
  driver: 'Driver phone',
  other: 'Other',
};

export const PhoneStatusSchema = z.enum(['issued', 'spare', 'lost', 'retired']);
export type PhoneStatus = z.infer<typeof PhoneStatusSchema>;
export const PHONE_STATUS_LABELS: Record<PhoneStatus, string> = {
  issued: 'Issued',
  spare: 'Spare',
  lost: 'Lost or stolen',
  retired: 'Retired',
};

export const PhoneSeatSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  title: z.string(),
  kind: z.string(),
});

export const PhoneSchema = z.object({
  id: z.uuid(),
  /** The IT asset number ("Phone ID" on the old sheet). */
  assetNo: z.number().int().nullable(),
  /** 11-digit UK mobile number as dialled, e.g. 07860657025. */
  number: z.string(),
  sim: z.string().nullable(),
  kind: PhoneKindSchema,
  status: PhoneStatusSchema,
  /** The seat the handset belongs to (branch handsets). */
  seat: PhoneSeatSchema.nullable(),
  /** Who holds the seat today, so who has the phone unless `person` or `holderNote` say otherwise. */
  seatHolder: PersonRefSchema.nullable(),
  /** A named person the handset was given to (overrides the seat holder). */
  person: PersonRefSchema.nullable(),
  /** The site the handset belongs to: the seat's, else the one chosen. */
  site: SiteRefSchema.nullable(),
  region: z.object({ id: z.uuid(), code: z.string(), name: z.string() }).nullable(),
  /** "Left at the branch", "In the safe", "With GF": where it really is when not with the holder. */
  holderNote: z.string().nullable(),
  m365Username: z.string().nullable(),
  /** A password is stored for the Office 365 account (never the value). */
  hasPassword: z.boolean(),
  notes: z.string().nullable(),
  updatedAt: Instant,
});
export type Phone = z.infer<typeof PhoneSchema>;

export const PhonesResponseSchema = z.object({
  phones: z.array(PhoneSchema),
  /** True when the viewer may add, edit, assign and reveal passwords. */
  canManage: z.boolean(),
  /** Set on the server (FLEET_SECRETS_KEY), so the UI can say when passwords cannot be stored. */
  secretsConfigured: z.boolean(),
});
export type PhonesResponse = z.infer<typeof PhonesResponseSchema>;

export const PhonesQuerySchema = z.object({
  siteId: z.uuid().optional(),
  personId: z.uuid().optional(),
});
export type PhonesQuery = z.infer<typeof PhonesQuerySchema>;

export const PhoneHistoryEventSchema = z.object({
  at: Instant,
  by: PersonRefSchema.nullable(),
  action: z.string(),
  /** What changed, field → [before, after], already reduced to the fields that differ. */
  changes: z.record(z.string(), z.tuple([z.unknown(), z.unknown()])),
});
export type PhoneHistoryEvent = z.infer<typeof PhoneHistoryEventSchema>;

export const PhoneDetailSchema = PhoneSchema.extend({
  history: z.array(PhoneHistoryEventSchema),
});
export type PhoneDetail = z.infer<typeof PhoneDetailSchema>;

const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === '' ? null : s))
    .nullable()
    .optional();

/** Accepts "07860 657025", "+44 7860 657025", "447860657025"; stores 07860657025. */
export function normaliseUkMobile(raw: string): string | null {
  const digits = raw.replace(/[^\d+]/g, '');
  let n = digits.startsWith('+44') ? `0${digits.slice(3)}` : digits.replace(/^\+/, '');
  if (/^44\d{10}$/.test(n)) n = `0${n.slice(2)}`;
  if (/^7\d{9}$/.test(n)) n = `0${n}`;
  return /^0\d{10}$/.test(n) ? n : null;
}

const NumberField = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .transform((s, ctx) => {
    const n = normaliseUkMobile(s);
    if (!n) {
      ctx.addIssue({ code: 'custom', message: 'Enter an 11-digit UK mobile number' });
      return z.NEVER;
    }
    return n;
  });

export const PhoneWriteSchema = z.object({
  number: NumberField,
  assetNo: z.number().int().min(0).max(999_999).nullable().optional(),
  sim: optText(32),
  kind: PhoneKindSchema,
  status: PhoneStatusSchema.optional(),
  /** Exactly one way to place a handset: a seat, a person, a site, or nothing (spare). */
  seatId: z.uuid().nullable().optional(),
  personId: z.uuid().nullable().optional(),
  siteId: z.uuid().nullable().optional(),
  holderNote: optText(200),
  m365Username: optText(190),
  notes: optText(2000),
});
export type PhoneWrite = z.infer<typeof PhoneWriteSchema>;

export const PhonePatchSchema = PhoneWriteSchema.partial();
export type PhonePatch = z.infer<typeof PhonePatchSchema>;

export const PhoneIdResponseSchema = z.object({ id: z.uuid() });

export const PhonePasswordSetSchema = z.object({ value: z.string().min(1).max(128) });
export const PhonePasswordRevealResponseSchema = z.object({ value: z.string() });
export const PhonePasswordSetResponseSchema = z.object({
  ok: z.literal(true),
  replaced: z.boolean(),
});

/** Seats a handset can belong to and sites it can sit at, for the form. */
export const PhoneOptionsSchema = z.object({
  seats: z.array(
    PhoneSeatSchema.extend({
      site: SiteRefSchema,
      holder: PersonRefSchema.nullable(),
      /** The seat already has a handset of this kind; choosing it moves the number. */
      phoneId: z.uuid().nullable(),
    }),
  ),
  sites: z.array(SiteRefSchema),
});
export type PhoneOptions = z.infer<typeof PhoneOptionsSchema>;
